"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  type SelectChangeEvent,
  Tooltip,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Slider,
} from "@mui/material"
import {
  Fullscreen,
  FullscreenExit,
  Refresh,
  Login,
  Logout,
  MoreVert,
  FilterList,
  GetApp,
  ExpandMore,
  BugReport,
  Warning,
  Height,
} from "@mui/icons-material"
import { PowerBIEmbed } from "powerbi-client-react"
import * as pbi from "powerbi-client"
import { powerBiConfig } from "./config/authConfig"
import { useAuth } from "./hooks/useAuth"
import { EmbedConfig, PowerBIService } from "./services/powerBiService"


interface PowerBIDashboardProps {
  workspaceId?: string
  reportId?: string
  title?: string
  height?: string | number
}

const PowerBIDashboard: React.FC<PowerBIDashboardProps> = ({
  workspaceId = powerBiConfig.workspaceId,
  reportId = powerBiConfig.reportId,
  title = "Power BI Dashboard",
  height = "calc(100vh - 280px)", // Dynamic height calculation
}) => {
  const { isAuthenticated, accessToken, isLoading: authLoading, login, logout, refreshToken, user } = useAuth()

  const [embedConfig, setEmbedConfig] = useState<EmbedConfig | null>(null)
  const [reports, setReports] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingReports, setIsLoadingReports] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null)
  const [selectedReportId, setSelectedReportId] = useState(reportId)
  const [viewMode, setViewMode] = useState<string>("view")
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [debugInfo, setDebugInfo] = useState<any>(null)
  const [useAlternativeMethod, setUseAlternativeMethod] = useState(false)
  const [customHeight, setCustomHeight] = useState(900) // Custom height control (increased from 800)
  const [autoHeight, setAutoHeight] = useState(true) // Auto height toggle

  // Refs to prevent multiple simultaneous calls
  const reportRef = useRef<any>(null)
  const tokenRefreshInterval = useRef<NodeJS.Timeout | null>(null)
  const isLoadingRef = useRef(false)
  const isLoadingReportsRef = useRef(false)
  const lastLoadedConfigRef = useRef<string>("")
  const containerRef = useRef<HTMLDivElement>(null)

  // Memoized service instance
  const powerBiService = useRef<PowerBIService | null>(null)

  // Calculate dynamic height based on viewport
  const calculateHeight = useCallback(() => {
    if (isFullscreen) {
      return "calc(100vh - 80px)" // Account for header in fullscreen
    }

    if (autoHeight) {
      // Calculate based on available viewport height minus header, nav, and padding
      const viewportHeight = window.innerHeight
      const headerHeight = 64 // AppBar height
      const titleSectionHeight = 120 // Title section height
      const controlsHeight = 100 // Controls height (increased to account for all controls)
      const padding = 60 // Additional padding

      const availableHeight = viewportHeight - headerHeight - titleSectionHeight - controlsHeight - padding
      return Math.max(700, availableHeight) // Minimum 700px (increased from 600px)
    }

    return customHeight
  }, [isFullscreen, autoHeight, customHeight])

  // Update service when token changes
  useEffect(() => {
    if (accessToken) {
      powerBiService.current = new PowerBIService(accessToken)
    } else {
      powerBiService.current = null
    }
  }, [accessToken])

  // Load reports only once when authenticated
  const loadReports = useCallback(async () => {
    if (!powerBiService.current || isLoadingReportsRef.current || !workspaceId) return

    isLoadingReportsRef.current = true
    setIsLoadingReports(true)
    setError(null)

    try {
      const reportsData = await powerBiService.current.getReports(workspaceId)
      setReports(reportsData)
    } catch (err: any) {
      console.error("Error loading reports:", err)
      setError(err.message || "Failed to load reports")
    } finally {
      setIsLoadingReports(false)
      isLoadingReportsRef.current = false
    }
  }, [workspaceId])

  // Load embed config with both methods
  const loadEmbedConfig = useCallback(async () => {
    if (!powerBiService.current || !workspaceId || !selectedReportId || isLoadingRef.current) return

    const configKey = `${workspaceId}-${selectedReportId}`
    if (lastLoadedConfigRef.current === configKey && embedConfig) {
      return
    }

    isLoadingRef.current = true
    setIsLoading(true)
    setError(null)

    try {
      let config: EmbedConfig

      if (useAlternativeMethod) {
        console.log("Using alternative embed token method...")
        config = await powerBiService.current.getEmbedTokenAlternative(workspaceId, selectedReportId)
      } else {
        console.log("Using standard embed token method...")
        config = await powerBiService.current.getEmbedToken(workspaceId, selectedReportId)
      }

      if (config) {
        setEmbedConfig(config)
        setTokenExpiry(new Date(config.tokenExpiry))
        lastLoadedConfigRef.current = configKey
        setupTokenRefresh(config.tokenExpiry)
      }
    } catch (err: any) {
      console.error("Error loading embed config:", err)

      // If standard method fails with permission error, suggest trying alternative
      if (!useAlternativeMethod && err.message.includes("Report.Read.All")) {
        setError(
          `${err.message}\n\nTip: Try enabling 'Alternative Method' toggle above, or ensure your Azure AD app has proper permissions.`,
        )
      } else {
        setError(err.message || "Failed to load Power BI configuration")
      }
    } finally {
      setIsLoading(false)
      isLoadingRef.current = false
    }
  }, [workspaceId, selectedReportId, embedConfig, useAlternativeMethod])

  const setupTokenRefresh = useCallback(
    (expiry: string) => {
      if (tokenRefreshInterval.current) {
        clearInterval(tokenRefreshInterval.current)
      }

      const expiryTime = new Date(expiry).getTime()
      const currentTime = new Date().getTime()
      const refreshTime = expiryTime - currentTime - 5 * 60 * 1000

      if (refreshTime > 0) {
        tokenRefreshInterval.current = setTimeout(async () => {
          try {
            const newToken = await refreshToken()
            if (newToken && reportRef.current) {
              await reportRef.current.setAccessToken(newToken)
              setSnackbarMessage("Token refreshed successfully")
              setSnackbarOpen(true)
            }
          } catch (error) {
            console.error("Token refresh failed:", error)
            setError("Session expired. Please refresh the page.")
          }
        }, refreshTime)
      }
    },
    [refreshToken],
  )

  // Test API access with detailed results
  const testApiAccess = useCallback(async () => {
    if (!powerBiService.current) return

    try {
      const result = await powerBiService.current.testApiAccess()

      setDebugInfo({
        ...result,
        token: accessToken ? `${accessToken.substring(0, 20)}...` : "No token",
        workspaceId,
        reportId: selectedReportId,
        timestamp: new Date().toISOString(),
        alternativeMethod: useAlternativeMethod,
      })

      if (!result.success) {
        setError(`API Access Test Failed: ${result.message}`)
      } else {
        setSnackbarMessage("API access test passed!")
        setSnackbarOpen(true)
      }
    } catch (err: any) {
      setError(`API test failed: ${err.message}`)
    }
  }, [accessToken, workspaceId, selectedReportId, useAlternativeMethod])

  // Handle window resize for auto height
  useEffect(() => {
    const handleResize = () => {
      if (autoHeight && reportRef.current) {
        // Force re-render with new height
        const newHeight = calculateHeight()
        if (containerRef.current) {
          containerRef.current.style.height = typeof newHeight === "number" ? `${newHeight}px` : newHeight
        }
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [autoHeight, calculateHeight])

  // Main effect - only run when authentication state changes
  useEffect(() => {
    if (isAuthenticated && accessToken && !isLoadingRef.current) {
      loadReports()
    }

    return () => {
      if (tokenRefreshInterval.current) {
        clearInterval(tokenRefreshInterval.current)
      }
    }
  }, [isAuthenticated, accessToken, loadReports])

  // Separate effect for embed config - only when report selection changes
  useEffect(() => {
    if (isAuthenticated && accessToken && selectedReportId && workspaceId) {
      const configKey = `${workspaceId}-${selectedReportId}`
      if (lastLoadedConfigRef.current !== configKey) {
        loadEmbedConfig()
      }
    }
  }, [isAuthenticated, accessToken, selectedReportId, workspaceId, useAlternativeMethod, loadEmbedConfig])

  const handleEmbedded = (report: any) => {
    console.log("Report embedded successfully")
    reportRef.current = report
  }

  const handleError = (error: any) => {
    console.error("Power BI embed error:", error)
    setError("Failed to load Power BI dashboard")
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const refreshReport = async () => {
    if (reportRef.current) {
      try {
        await reportRef.current.refresh()
        setSnackbarMessage("Report refreshed successfully")
        setSnackbarOpen(true)
      } catch (error) {
        console.error("Error refreshing report:", error)
        setError("Failed to refresh report")
      }
    } else {
      lastLoadedConfigRef.current = ""
      setEmbedConfig(null)
      loadEmbedConfig()
    }
  }

  const handleReportChange = (event: SelectChangeEvent) => {
    const newReportId = event.target.value
    setSelectedReportId(newReportId)
    lastLoadedConfigRef.current = ""
    setEmbedConfig(null)
  }

  const handleViewModeChange = (event: SelectChangeEvent) => {
    setViewMode(event.target.value)
    if (reportRef.current) {
      const newViewMode = event.target.value === "edit" ? pbi.models.ViewMode.Edit : pbi.models.ViewMode.View
      reportRef.current.switchMode(newViewMode)
    }
  }

  const toggleFilters = async () => {
    if (reportRef.current) {
      try {
        const settings = await reportRef.current.getSettings()
        const newFilterState = !settings.panes.filters.visible

        await reportRef.current.updateSettings({
          panes: {
            filters: {
              visible: newFilterState,
            },
          },
        })

        setSnackbarMessage(`Filters ${newFilterState ? "shown" : "hidden"}`)
        setSnackbarOpen(true)
      } catch (error) {
        console.error("Error toggling filters:", error)
      }
    }
  }

  const exportReport = async () => {
    if (reportRef.current) {
      try {
        await reportRef.current.print()
      } catch (error) {
        console.error("Error exporting report:", error)
        setError("Failed to export report")
      }
    }
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const getTimeUntilExpiry = () => {
    if (!tokenExpiry) return null
    const now = new Date().getTime()
    const expiry = tokenExpiry.getTime()
    const diff = expiry - now

    if (diff <= 0) return "Expired"

    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`
    }
    return `${minutes}m`
  }

  if (authLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Initializing authentication...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  if (!isAuthenticated) {
    return (
      <Card>
        <CardHeader title="Authentication Required" />
        <CardContent>
          <Box textAlign="center" py={4}>
            <Typography variant="body1" gutterBottom>
              Please sign in to view the Power BI dashboard
            </Typography>
            <Button variant="contained" startIcon={<Login />} onClick={login} sx={{ mt: 2 }}>
              Sign In with Microsoft
            </Button>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    )
  }

  const powerBiEmbedConfig = embedConfig
    ? {
      type: "report",
      id: embedConfig.id,
      embedUrl: embedConfig.embedUrl,
      accessToken: embedConfig.accessToken,
      tokenType: pbi.models.TokenType.Embed,
      settings: {
        panes: {
          filters: {
            expanded: false,
            visible: true,
          },
          pageNavigation: {
            visible: true,
          },
        },
        background: pbi.models.BackgroundType.Transparent,
        layoutType: pbi.models.LayoutType.Custom,
        customLayout: {
          displayOption: pbi.models.DisplayOption.FitToWidth,
        },
      },
    }
    : null

  const currentHeight = calculateHeight()

  return (
    <>
      <Card
        sx={{
          width: "100%",
          height: isFullscreen ? "100vh" : "auto",
          position: isFullscreen ? "fixed" : "relative",
          top: isFullscreen ? 0 : "auto",
          left: isFullscreen ? 0 : "auto",
          zIndex: isFullscreen ? 9999 : "auto",
          margin: isFullscreen ? 0 : "auto",
        }}
      >
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="h6" component="div">
                {title}
              </Typography>

              {reports.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Select Report</InputLabel>
                  <Select value={selectedReportId} label="Select Report" onChange={handleReportChange}>
                    {reports.map((report) => (
                      <MenuItem key={report.id} value={report.id}>
                        {report.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Mode</InputLabel>
                <Select value={viewMode} label="Mode" onChange={handleViewModeChange}>
                  <MenuItem value="view">View</MenuItem>
                  <MenuItem value="edit">Edit</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={useAlternativeMethod}
                    onChange={(e) => {
                      setUseAlternativeMethod(e.target.checked)
                      lastLoadedConfigRef.current = ""
                      setEmbedConfig(null)
                    }}
                    size="small"
                  />
                }
                label="Alternative Method"
              />

              <FormControlLabel
                control={<Switch checked={autoHeight} onChange={(e) => setAutoHeight(e.target.checked)} size="small" />}
                label="Auto Height"
              />

              {tokenExpiry && (
                <Chip
                  label={`Token expires in: ${getTimeUntilExpiry()}`}
                  size="small"
                  color={getTimeUntilExpiry() === "Expired" ? "error" : "default"}
                />
              )}

              {user && <Chip label={user.name || user.username} size="small" color="primary" />}
            </Box>
          }
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Test API Access">
                <IconButton onClick={testApiAccess}>
                  <BugReport />
                </IconButton>
              </Tooltip>

              <Tooltip title="Toggle Filters">
                <IconButton onClick={toggleFilters}>
                  <FilterList />
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh">
                <IconButton onClick={refreshReport} disabled={isLoading}>
                  <Refresh />
                </IconButton>
              </Tooltip>

              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton onClick={toggleFullscreen}>{isFullscreen ? <FullscreenExit /> : <Fullscreen />}</IconButton>
              </Tooltip>

              <IconButton onClick={handleMenuClick}>
                <MoreVert />
              </IconButton>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem
                  onClick={() => {
                    exportReport()
                    handleMenuClose()
                  }}
                >
                  <GetApp sx={{ mr: 1 }} />
                  Export Report
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    logout()
                    handleMenuClose()
                  }}
                >
                  <Logout sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          }
          sx={{ pb: 1 }}
        />

        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {/* Height Control Panel */}
          {!autoHeight && (
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Height />
                <Typography variant="body2">Height: {customHeight}px</Typography>
                <Slider
                  value={customHeight}
                  onChange={(_, value) => setCustomHeight(value as number)}
                  min={500}
                  max={1500}
                  step={50}
                  sx={{ width: 200 }}
                />
              </Box>
            </Box>
          )}

          <Box position="relative">
            {(isLoading || isLoadingReports || !embedConfig) && (
              <Box display="flex" justifyContent="center" alignItems="center" height={currentHeight} bgcolor="grey.50">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {isLoadingReports ? "Loading reports..." : "Loading dashboard..."}
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ m: 2 }} icon={<Warning />}>
                <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
                  {error}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    onClick={() => {
                      setError(null)
                      lastLoadedConfigRef.current = ""
                      setEmbedConfig(null)
                      loadEmbedConfig()
                    }}
                    size="small"
                    variant="outlined"
                  >
                    Retry
                  </Button>
                </Box>

                {debugInfo && (
                  <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="body2">Debug Information</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <pre style={{ fontSize: "11px", overflow: "auto", maxHeight: "300px" }}>
                        {JSON.stringify(debugInfo, null, 2)}
                      </pre>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Alert>
            )}

            {powerBiEmbedConfig && !isLoading && !isLoadingReports && (
              <Box
                ref={containerRef}
                sx={{
                  height: isFullscreen ? "calc(100vh - 80px)" : currentHeight,
                  minHeight: 700,
                  width: "100%",
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <PowerBIEmbed
                  embedConfig={powerBiEmbedConfig}
                  eventHandlers={
                    new Map([
                      ["loaded", () => console.log("Report loaded")],
                      ["rendered", () => handleEmbedded(reportRef.current)],
                      ["error", handleError],
                      ["dataSelected", (event) => console.log("Data selected:", event.detail)],
                    ])
                  }
                  cssClassName="powerbi-embed-fullheight"
                  getEmbeddedComponent={(embeddedReport) => {
                    reportRef.current = embeddedReport
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  )
}

export default PowerBIDashboard
